import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { actionCreators as modalActions } from 'react/api/actions/modal';
import { actionCreators as recoverActions } from 'react/api/actions/recoverPassword';
import { actionCreators as i18nActions } from 'react/api/actions/i18n';

import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Flex,
  Input,
  PasswordInput,
  Text
} from 'onefaceui-react-components';
import PasswordValidator from 'react/components/PasswordValidator';

function ChangePasswordModal({ isVisible, login, resourceError }) {
  const dispatch = useDispatch();

  const changedPasswordSuccessfully = useSelector(({ recoverPassword }) => recoverPassword.get('success'));
  const oldPassword = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'oldPass']));
  const newPassword = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'newPass']));
  const passwordConfirmation = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'confirmPass']));
  const hintsValidated = useSelector(({ recoverPassword }) => recoverPassword.getIn(['password', 'hintsValidated']));
  const isLoading = useSelector(({ recoverPassword }) => recoverPassword.get('loading'));
  const errorResource = useSelector(({ recoverPassword }) => recoverPassword.get('errorMessage'));
  const i18n = useSelector((state) => state.i18n.get('resources'));

  const arePasswordsEquals = useMemo(
    () => newPassword && passwordConfirmation && newPassword === passwordConfirmation,
    [newPassword, passwordConfirmation]
  );

  const newPasswordIsSameOldPassword = useMemo(
    () => !!newPassword && !!oldPassword && newPassword === oldPassword,
    [newPassword, oldPassword]
  );

  const newPasswordAndConfirmPasswordDiff = useMemo(
    () => (!!newPassword && !!passwordConfirmation) && newPassword !== passwordConfirmation,
    [newPassword, passwordConfirmation]
  );

  const buttonEnabled = useMemo(
    () => arePasswordsEquals && hintsValidated
    && !newPasswordIsSameOldPassword && !newPasswordAndConfirmPasswordDiff,
    [
      arePasswordsEquals,
      hintsValidated,
      newPasswordIsSameOldPassword,
      newPasswordAndConfirmPasswordDiff
    ]
  );

  const saveButtonClassName = useMemo(
    () => (isLoading ? 'loader-spinner' : ''),
    [isLoading]
  );

  const errorMessage = useMemo(
    () => i18n.get(errorResource) || i18n.get(resourceError),
    [errorResource, resourceError]
  );

  const onChange = ({ target: { name, value } }) => {
    dispatch(recoverActions.onChange({ value, id: name }));
  };

  const allHintsValidated = (value) => {
    dispatch(recoverActions.onChange({ value, id: 'hintsValidated' }));
  };

  const handleCloseModal = () => {
    dispatch(modalActions.changeIsOpen(false));
  };

  useEffect(() => {
    dispatch(
      i18nActions.fetchResourcesByKey([
        'users.login',
        'user.changePassword.oldPassword',
        'user.changePassword.newPassword',
        'user.changePassword.confirmNewPassword',
        'user.changePassword.sendNewPasswordBtn',
        'general.button.cancel',
        'user.credentialsExpired',
        'AbstractUserDetailsAuthenticationProvider.badCredentials'
      ])
    );
  }, [dispatch]);

  const handleSubmit = () => {
    const url = '/User/ChangePassword';
    const payload = {
      login,
      oldPassword,
      password: newPassword,
      passwordConfirmation
    };

    dispatch(recoverActions.requestChangePassword({ url, payload }));
  };

  const resetReduxState = () => {
    setTimeout(() => {
      dispatch(recoverActions.resetState());
    }, 400);
  };

  useEffect(() => {
    if (changedPasswordSuccessfully) {
      resetReduxState();
    }
  }, [changedPasswordSuccessfully]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="blockerBackground" data-testid="change-password-modal">
      <button
        type="button"
        data-testid="close-modal-btn"
        className="login closeModalWindow"
        onClick={handleCloseModal}
      />
      <div className="popupFormContainer login changePasswordModal">
        <Flex flexDirection="column" gap={8}>
          <Flex shouldShow={!!resourceError}>
            <Text type="danger" size="xs" dataTestId="change-password-error">
              {errorMessage}
            </Text>
          </Flex>
          <Flex flexDirection="column" gap={8}>
            <Input
              label={i18n.get('users.login')}
              disabled
              value={login}
              name="login"
              dataTestId="input-login"
              className="oneface-input"
            />
            <PasswordInput
              label={i18n.get('user.changePassword.oldPassword')}
              name="oldPass"
              dataTestId="input-old-password"
              value={oldPassword}
              onChange={onChange}
              className="oneface-input"
            />
            <PasswordInput
              label={i18n.get('user.changePassword.newPassword')}
              name="newPass"
              value={newPassword}
              onChange={onChange}
              className="oneface-input"
              dataTestId="input-new-password"
            />
            <PasswordValidator
              user={login}
              maxPerCol={3}
              newPass={newPassword}
              allHintsValidated={allHintsValidated}
            />
            <PasswordInput
              label={i18n.get('user.changePassword.confirmNewPassword')}
              name="confirmPass"
              value={passwordConfirmation}
              onChange={onChange}
              className="oneface-input"
              dataTestId="input-confirm-password"
            />
            <Flex justifyContent="space-between" gap={8} className="changePasswordFooter">
              <Button
                onClick={handleCloseModal}
                dataTestId="button-cancel"
                variant="secondary"
              >
                {i18n.get('general.button.cancel')}
              </Button>
              <Button
                loading={saveButtonClassName}
                onClick={handleSubmit}
                disabled={!buttonEnabled}
                dataTestId="button-save"
              >
                {i18n.get('user.changePassword.sendNewPasswordBtn')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </div>
    </div>
  );
}

ChangePasswordModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  login: PropTypes.string.isRequired,
  resourceError: PropTypes.string.isRequired
};

export default ChangePasswordModal;
